import type { EstadoPortaOficina, IdZonaOperacaoPorta } from '../../dominio/didatico/oficinaCorte'
import { ANGULO_ABERTURA_PORTA } from '../../dominio/didatico/obstaculosExternos'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { deArray, girarEmTorno, paraArray, somar, subtrair, vetor } from '../../dominio/geometria/vetor3'
import { dadosDaPorta, type IdPortaOficina } from './zonasOperacaoPorta'

/** Abertura máxima da porta na oficina, em radianos. */
export const ABERTURA_RAD_PORTA = ANGULO_ABERTURA_PORTA

/** Distância da origem do grupo da porta até o plano dos marcadores. */
const FOLGA_BASE = 0.07

/** Sobra entre o marcador e a superfície, para o alvo de toque não raspar na chapa. */
const SOBRA = 0.03

/**
 * Quanto a chapa externa se desloca para fora, no X local da porta.
 *
 * Mora aqui, e não na `PortaDidatica`, porque os marcadores precisam do mesmo
 * número: é o deslocamento da chapa que decide a que distância eles têm de
 * ficar para continuar alcançáveis.
 */
export function afastamentoDaChapa(estado: EstadoPortaOficina | undefined): number {
  if (estado?.chapa === 'desfolhada') return 0.14
  if (estado?.chapa !== 'deformada') return 0
  if (estado.tecnicaCriacaoEspaco === 'pincamento') return 0.11
  if (estado.tecnicaCriacaoEspaco === 'amassamentoPorta') return 0.08
  return 0.055
}

/** Zonas cuja peça é da carroceria: ficam paradas quando a porta gira. */
export const ZONAS_DA_CARROCERIA: readonly IdZonaOperacaoPorta[] = ['paralama']

export interface QuadroDaPorta {
  /** Eixo da dobradiça, no referencial do veículo. */
  origem: [number, number, number]
  /** Abertura atual da porta, em radianos. */
  giroY: number
  /** Afastamento lateral do marcador, para ele ficar fora da superfície atual. */
  folgaLateral: number
  lado: 'esquerdo' | 'direito'
}

/**
 * Pose viva da porta, para marcadores e ferramenta acompanharem a peça.
 *
 * As zonas nascem em coordenadas fixas do veículo — a porta fechada —, mas a
 * porta gira na dobradiça e a chapa se desloca para fora. Sem esta conversão o
 * marcador da trava, lá no extremo traseiro, ficava ~12 cm atrás da chapa
 * assim que a abertura era criada: o anel continuava desenhado (não tem teste
 * de profundidade), o núcleo sumia e o alvo de toque deixava de receber o
 * ponteiro. Como `expandirTrava` exige abertura, a trava só podia ser liberada
 * depois de ficar inalcançável, e a porta nunca ficava livre.
 */
export function quadroDaPorta(
  perfil: PerfilVeiculo,
  idPorta: IdPortaOficina,
  estado?: EstadoPortaOficina,
): QuadroDaPorta {
  const { lado, faixa } = dadosDaPorta(perfil, idPorta)
  const sinal = lado === 'esquerdo' ? 1 : -1
  const giro = (sinal < 0 ? ABERTURA_RAD_PORTA : -ABERTURA_RAD_PORTA) * (estado?.abertura ?? 0)

  return {
    origem: [sinal * (perfil.largura / 2 - 0.015), 0, faixa.zDianteiro],
    giroY: giro,
    // os marcadores internos são irmãos da chapa e não acompanham o
    // deslocamento dela: sem esta folga, a chapa deformada passa por cima
    folgaLateral: Math.max(FOLGA_BASE, afastamentoDaChapa(estado) + SOBRA) - FOLGA_BASE,
    lado,
  }
}

/** Posição canônica (porta fechada) levada para onde a porta está agora. */
export function posicaoNoQuadro(
  quadro: QuadroDaPorta,
  canonica: [number, number, number],
): [number, number, number] {
  const sinal = quadro.lado === 'esquerdo' ? 1 : -1
  const origem = deArray(quadro.origem)
  const afastada = somar(deArray(canonica), vetor(sinal * quadro.folgaLateral, 0, 0))
  const local = subtrair(afastada, origem)

  return paraArray(somar(girarEmTorno(local, vetor(0, 1, 0), quadro.giroY), origem))
}

/** Volta de um ponto na cena para a coordenada canônica da porta fechada. */
export function canonicaDoQuadro(
  quadro: QuadroDaPorta,
  ponto: [number, number, number],
): [number, number, number] {
  const sinal = quadro.lado === 'esquerdo' ? 1 : -1
  const origem = deArray(quadro.origem)
  const local = girarEmTorno(subtrair(deArray(ponto), origem), vetor(0, 1, 0), -quadro.giroY)

  return paraArray(subtrair(somar(local, origem), vetor(sinal * quadro.folgaLateral, 0, 0)))
}
